import React, { useContext, useMemo } from 'react'
import { RichUtils } from 'draft-js'
import { EditorContext } from '.'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBold } from '@fortawesome/pro-solid-svg-icons/faBold'
import { faItalic } from '@fortawesome/pro-solid-svg-icons/faItalic'
import { faUnderline } from '@fortawesome/pro-solid-svg-icons/faUnderline'
import { faListUl } from '@fortawesome/pro-solid-svg-icons/faListUl'
import { faListOl } from '@fortawesome/pro-solid-svg-icons/faListOl'
import injectSheet from 'react-jss'
import {
  controlButtonStyles,
  controlPanelStyles,
  separatorStyles
} from './controls.styles'
import classNames from 'classnames'

const ControlPanel = injectSheet(controlPanelStyles)(
  ({ children, classes, className, sticky }) => (
    <div
      className={classNames(
        classes.container,
        { [classes.sticky]: sticky },
        className
      )}>
      <div className={classes.holder}>{children}</div>
    </div>
  )
)

const ControlButton = injectSheet(controlButtonStyles)(
  ({ children, action, classes, active }) => (
    <span
      className={classNames(classes.button, {
        [classes.active]: active
      })}
      role="button"
      onMouseDown={e => {
        e.preventDefault()
        action && action()
      }}>
      <span className={classes.content}>{children}</span>
    </span>
  )
)

const ControlSeparator = injectSheet(separatorStyles)(({ classes }) => (
  <div className={classes.separator} />
))

const Bold = () => {
  const { editorState, onChange } = useContext(EditorContext)
  const hasBold = useMemo(
    () => {
      return editorState.getCurrentInlineStyle().has('BOLD')
    },
    [editorState]
  )
  return (
    <ControlButton
      active={hasBold}
      action={() => {
        const newState = RichUtils.toggleInlineStyle(editorState, 'BOLD')
        onChange(newState)
      }}>
      <FontAwesomeIcon icon={faBold} />
    </ControlButton>
  )
}

const Italic = () => {
  const { editorState, onChange } = useContext(EditorContext)
  const hasItalic = useMemo(
    () => {
      return editorState.getCurrentInlineStyle().has('ITALIC')
    },
    [editorState]
  )
  return (
    <ControlButton
      active={hasItalic}
      action={() => {
        const newState = RichUtils.toggleInlineStyle(editorState, 'ITALIC')
        onChange(newState)
      }}>
      <FontAwesomeIcon icon={faItalic} />
    </ControlButton>
  )
}

const Underline = () => {
  const { editorState, onChange } = useContext(EditorContext)
  const hasItalic = useMemo(
    () => {
      return editorState.getCurrentInlineStyle().has('UNDERLINE')
    },
    [editorState]
  )
  return (
    <ControlButton
      active={hasItalic}
      action={() => {
        const newState = RichUtils.toggleInlineStyle(editorState, 'UNDERLINE')
        onChange(newState)
      }}>
      <FontAwesomeIcon icon={faUnderline} />
    </ControlButton>
  )
}

const UnorderedList = () => {
  const { editorState, onChange } = useContext(EditorContext)
  const blockType = useMemo(
    () => {
      const selectionState = editorState.getSelection()
      const anchorKey = selectionState.getAnchorKey()
      const currentContent = editorState.getCurrentContent()
      const currentContentBlock = currentContent.getBlockForKey(anchorKey)
      return currentContentBlock.getType()
    },
    [editorState]
  )
  return (
    <ControlButton
      active={blockType === 'unordered-list-item'}
      action={() => {
        const newState = RichUtils.toggleBlockType(
          editorState,
          'unordered-list-item'
        )
        onChange(newState)
      }}>
      <FontAwesomeIcon icon={faListUl} />
    </ControlButton>
  )
}

const OrderedList = () => {
  const { editorState, onChange } = useContext(EditorContext)
  const blockType = useMemo(
    () => {
      const selectionState = editorState.getSelection()
      const anchorKey = selectionState.getAnchorKey()
      const currentContent = editorState.getCurrentContent()
      const currentContentBlock = currentContent.getBlockForKey(anchorKey)
      return currentContentBlock.getType()
    },
    [editorState]
  )
  return (
    <ControlButton
      active={blockType === 'ordered-list-item'}
      action={() => {
        const newState = RichUtils.toggleBlockType(
          editorState,
          'ordered-list-item'
        )
        onChange(newState)
      }}>
      <FontAwesomeIcon icon={faListOl} />
    </ControlButton>
  )
}

export default {
  Bold,
  Italic,
  Underline,
  UnorderedList,
  OrderedList,
  Panel: ControlPanel,
  Separator: ControlSeparator
}
